import React, { useMemo } from 'react';
import { Flex } from '@withjoy/joykit';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { Skeleton } from '@shared/components/Skeleton';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { AddedTag } from './AddedTag';

interface PhotoContainerProps {
  children: React.ReactNode;
  isMobileOrTablet?: boolean;
}

const Container = ({ children }: PhotoContainerProps) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      aspectRatio={{ _: '16/9', md: '.96/1.25' }}
      maxWidth={{ _: '100%', md: 220 }}
      minWidth={{ _: '100%', md: 220 }}
      position="relative"
    >
      {children}
    </Flex>
  );
};

interface ListingPhotosProps {
  listing: AccommodationListingDTO;
  isMobileOrTablet?: boolean;
}

const ListingPhotos = ({ listing, isMobileOrTablet }: ListingPhotosProps) => {
  const config = useAccommodationsConsoleConfig();
  const images = useMemo(() => {
    if (!listing.mediaAssets?.photos.length) {
      return [{ src: DEFAULT_HOTEL_IMAGE, alt: listing.name }];
    }

    return (
      listing.mediaAssets?.photos.map(x => ({
        src: x.url,
        alt: listing.name,
      })) || []
    );
  }, [listing.mediaAssets?.photos, listing.name]);

  return (
    <Container>
      {config.listingsPanel.listingEntry.shouldShowAddedTag && <AddedTag listing={listing} />}
      <PhotoCarousel
        imgProps={{
          borderRadius: '10px',
        }}
        aspectRatio={isMobileOrTablet ? '16/9' : '.96/1.25'}
        navigation={{
          variant: 'minimal',
          visible: 'default',
        }}
        images={images}
      />
    </Container>
  );
};

ListingPhotos.Skeleton = () => {
  return (
    <Container>
      <Skeleton
        borderRadius={'10px'}
        width="100%"
        height="100%"
      />
    </Container>
  );
};

export { ListingPhotos };
